/**
 * Analytics — theme-side publisher for custom tracking events.
 *
 * Feature modules call `trackEvent(EVENTS.listClick, payload, element)`. The
 * event is namespaced (`almada:<name>`) and handed to `Shopify.analytics.publish`;
 * the Web Pixels Manager forwards it to the custom pixel that owns GA4
 * (Shopify Admin > Settings > Customer events). The theme never loads gtag
 * and never decides consent: the pixel's Permission setting does.
 *
 * Never create `Shopify.analytics` or `Shopify.analytics.replayQueue` here.
 * Shopify's inline loader defines both from `content_for_header`; a theme-made
 * stub makes the loader skip setup and no pixel on the store initialises.
 *
 * Loaded as ES module via importmap alias `@theme/analytics`.
 * Event contract: GA4-EVENTS-PLAN.md.
 */

const NAMESPACE = 'almada';
export const MAX_PARAM_LENGTH = 100;
const MAX_DEPTH = 3;
const DEDUPE_WINDOW_MS = 300;
const RECENT_LIMIT = 50;
const QUEUE_LIMIT = 20;
const QUEUE_RETRY_MS = 500;
const QUEUE_RETRY_LIMIT = 5;
const DEBUG_STORAGE_KEY = 'theme:analytics-debug';
const PENDING_STORAGE_KEY = 'theme:analytics-pending';
const PENDING_MAX_AGE_MS = 60000;
const PENDING_LIMIT = 10;
const DEFERRED_FALLBACK_MS = 3000;
const PIXEL_READY_MESSAGE = 'almada:pixel-ready';
const PIXEL_READY_TIMEOUT_MS = 4000;

export const EVENTS = Object.freeze({
  listClick: 'list_click',
  filterApplied: 'filter_applied',
  videoStart: 'video_start',
  shopTheLookOpen: 'shop_the_look_open',
  shopTheLookProductClick: 'shop_the_look_product_click',
  contentLinkClick: 'content_link_click',
});

const KNOWN_EVENTS = new Set(Object.values(EVENTS));
const recentEvents = new Map();
const pendingQueue = [];
let queueFlushScheduled = false;
let queueRetries = 0;

function isDebug() {
  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function isEmptyContainer(value) {
  return Array.isArray(value) ? value.length === 0 : Object.keys(value).length === 0;
}

function sanitizeValue(value, depth) {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    const text = value.trim();
    return text ? text.slice(0, MAX_PARAM_LENGTH) : undefined;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'boolean') return value;
  if (depth >= MAX_DEPTH || typeof value !== 'object' || value instanceof Node) return undefined;

  const container = Array.isArray(value)
    ? value.map((item) => sanitizeValue(item, depth + 1)).filter((item) => item !== undefined)
    : sanitizeObject(value, depth + 1);
  return isEmptyContainer(container) ? undefined : container;
}

function sanitizeObject(source, depth = 0) {
  const clean = {};
  if (source == null || typeof source !== 'object' || Array.isArray(source)) return clean;

  for (const [key, value] of Object.entries(source)) {
    const sanitized = sanitizeValue(value, depth);
    if (sanitized !== undefined) clean[key] = sanitized;
  }
  return clean;
}

/**
 * Page-level parameters shared by every event. Sources: `window.theme.page`
 * (snippets/theme-runtime-config.liquid) and the Weglot proxy marker on <html>.
 */
export function pageContext() {
  const page = window.theme?.page ?? {};
  const html = document.documentElement;

  return sanitizeObject({
    page_type: page.type,
    template: page.templateSuffix ? `${page.template}.${page.templateSuffix}` : page.template,
    collection_handle: page.collectionHandle,
    // Not `language`: gtag treats that key as its built-in user-language field (`ul`).
    page_language: html.dataset.wgTranslated || page.language || html.lang,
    country: page.country,
    currency: window.theme?.marketMoney?.currency,
  });
}

/**
 * Section the element belongs to. `section_id` comes from Shopify's
 * `.shopify-section` wrapper, or from the theme's own `data-section-id` when
 * the node was moved out of its section (the shop-the-look popup lives under
 * <body>). `section_type` is reported only when a section root declares
 * `data-section-type="<section file name>"`: the wrapper id carries an editor
 * key (`section_image_text_AbC123`, `main`, a legacy number), not the type,
 * so deriving it from the id would mix formats in GA4.
 */
export function sectionContext(element) {
  if (!(element instanceof Element)) return {};

  const wrapper = element.closest('.shopify-section');
  const sectionId = wrapper
    ? wrapper.id.replace(/^shopify-section-/, '')
    : element.closest('[data-section-id]')?.dataset.sectionId;

  return sanitizeObject({
    section_id: sectionId,
    section_type: element.closest('[data-section-type]')?.dataset.sectionType,
  });
}

function shopifyAnalytics() {
  const analytics = window.Shopify?.analytics;
  return typeof analytics?.publish === 'function' ? analytics : null;
}

// Tracking must never throw into the UI handler that triggered it.
function publish(analytics, name, payload) {
  try {
    analytics.publish(name, payload);
  } catch (error) {
    if (isDebug()) console.warn('[analytics] publish failed:', error);
  }
}

function scheduleFlush() {
  if (queueFlushScheduled) return;
  queueFlushScheduled = true;

  if (document.readyState === 'complete') {
    setTimeout(flushQueue, QUEUE_RETRY_MS);
  } else {
    window.addEventListener('load', flushQueue, { once: true });
  }
}

function flushQueue() {
  queueFlushScheduled = false;
  const analytics = shopifyAnalytics();

  if (!analytics) {
    if (pendingQueue.length && queueRetries < QUEUE_RETRY_LIMIT) {
      queueRetries += 1;
      scheduleFlush();
    }
    return;
  }

  while (pendingQueue.length) {
    const [name, payload] = pendingQueue.shift();
    publish(analytics, name, payload);
  }
}

// Only reached when Shopify's loader is absent (theme dev proxy, HTML fixtures).
function enqueue(name, payload) {
  if (pendingQueue.length >= QUEUE_LIMIT) pendingQueue.shift();
  pendingQueue.push([name, payload]);
  scheduleFlush();
}

function isDuplicate(key, now) {
  const last = recentEvents.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;

  recentEvents.set(key, now);
  if (recentEvents.size > RECENT_LIMIT) {
    for (const [staleKey, time] of recentEvents) {
      if (now - time > DEDUPE_WINDOW_MS) recentEvents.delete(staleKey);
    }
  }
  return false;
}

function buildEvent(name, payload, element) {
  return {
    fullName: `${NAMESPACE}:${name}`,
    data: {
      ...pageContext(),
      ...sectionContext(element),
      ...sanitizeObject(payload),
    },
  };
}

function dispatch(fullName, data) {
  const analytics = shopifyAnalytics();
  if (analytics) {
    flushQueue();
    publish(analytics, fullName, data);
  } else {
    enqueue(fullName, data);
  }
}

function readPending() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PENDING_STORAGE_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((entry) => entry && typeof entry.name === 'string')
      : [];
  } catch {
    return [];
  }
}

function writePending(entries) {
  try {
    if (entries.length) {
      window.sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(entries));
    } else {
      window.sessionStorage.removeItem(PENDING_STORAGE_KEY);
    }
    return true;
  } catch {
    // storage blocked or full: the caller publishes immediately instead of deferring
    return false;
  }
}

// Publishes whatever an earlier document left behind. Runs once the custom pixel says
// it is subscribed (every page loads this module) and as a fallback timer in the
// document that deferred the event, in case the navigation never happened.
function drainPending() {
  const entries = readPending();
  if (!entries.length) return;
  writePending([]);
  const now = Date.now();
  entries
    .filter((entry) => now - entry.t < PENDING_MAX_AGE_MS)
    .forEach((entry) => dispatch(entry.name, entry.data));
}

/**
 * Publishes a theme event to Shopify's web pixels.
 *
 * @param {string} name one of `EVENTS` (snake_case, without the namespace)
 * @param {Object|null} [payload] event parameters. Strings are trimmed to 100
 *   chars; objects and arrays nest at most three levels (payload > items[] >
 *   item); DOM nodes, functions, empty strings and empty containers are dropped
 * @param {Element} [element] source element, used to derive the section context
 * @returns {boolean} true when the event was published or queued
 */
export function trackEvent(name, payload = {}, element = null) {
  if (typeof name !== 'string' || !name) return false;
  if (window.Shopify?.designMode) return false;

  const { fullName, data } = buildEvent(name, payload, element);
  if (isDuplicate(`${fullName}${JSON.stringify(data)}`, performance.now())) return false;

  if (isDebug()) {
    if (!KNOWN_EVENTS.has(name)) console.warn('[analytics] unknown event name:', name);
    // eslint-disable-next-line no-console -- opt-in debug output, gated by the localStorage flag in isDebug()
    console.debug('[analytics]', fullName, data);
  }

  dispatch(fullName, data);
  return true;
}

/**
 * Same as trackEvent for an interaction that immediately unloads the document (a
 * product link, a content link). The pixel's gtag batches hits and the sandbox iframe
 * gets no pagehide, so a hit published now would be lost; the event is stored and the
 * next document publishes it, tagged with this page's URL and title. A fallback timer
 * publishes it here if the navigation never happens (modifier click, cancelled); a
 * navigation whose response lands 3 to 3.5 s after the click can still lose the hit.
 *
 * @returns {boolean} true when the event was stored, published or queued
 */
export function trackNavigationEvent(name, payload = {}, element = null) {
  if (typeof name !== 'string' || !name) return false;
  if (window.Shopify?.designMode) return false;

  const { fullName, data } = buildEvent(name, payload, element);
  data.page_location = window.location.href;
  data.page_title = document.title;
  if (isDuplicate(`${fullName}${JSON.stringify(data)}`, performance.now())) return false;

  if (isDebug()) {
    // eslint-disable-next-line no-console -- opt-in debug output, gated by the localStorage flag in isDebug()
    console.debug('[analytics] deferred', fullName, data);
  }

  const entries = readPending().slice(-(PENDING_LIMIT - 1));
  entries.push({ name: fullName, data, t: Date.now() });
  if (!writePending(entries)) {
    dispatch(fullName, data);
    return true;
  }
  setTimeout(drainPending, DEFERRED_FALLBACK_MS);
  return true;
}

// Events published in the ~200 ms between the pixel iframe being created and the pixel
// code subscribing are dropped by the Web Pixels Manager (measured), so a fresh document
// waits for the pixel's ready message before publishing what the previous page left
// behind. The message arrives with an opaque origin (sandbox), so only its shape is
// checked. Without a message (pixel disconnected, blocked) the fallback publishes anyway;
// a navigation before that simply hands the entries to the next document.
function drainWhenPixelReady() {
  if (!readPending().length) return;

  const onMessage = (event) => {
    if (event.source === window || event.data?.type !== PIXEL_READY_MESSAGE) return;
    window.removeEventListener('message', onMessage);
    clearTimeout(fallback);
    drainPending();
  };
  window.addEventListener('message', onMessage);
  const fallback = setTimeout(() => {
    window.removeEventListener('message', onMessage);
    drainPending();
  }, PIXEL_READY_TIMEOUT_MS);
}

drainWhenPixelReady();
